import { Dispatch } from 'redux';
import { Services } from '../../types/services';

const SET_CONVERSION_OVERVIEW = 'SET_CONVERSION_OVERVIEW';
const SET_MODAL_CONVERSION = 'SET_MODAL_CONVERSION';
const TOGGLE_CONVERSION_ACTIVITY_CLEAR = 'TOGGLE_CONVERSION_ACTIVITY_CLEAR';
const TOGGLE_CONVERSION_ACTIVITY_COMPLETE = 'TOGGLE_CONVERSION_ACTIVITY_COMPLETE';
const TOGGLE_CONVERSION_ACTIVITY_FINAL = 'TOGGLE_CONVERSION_ACTIVITY_FINAL';
const TOGGLE_CONVERSION_ACTIVITY_LATER = 'TOGGLE_CONVERSION_ACTIVITY_LATER';
const TOGGLE_CONVERSION_ACTIVITY_ONE_WEEK = 'TOGGLE_CONVERSION_ACTIVITY_ONE_WEEK';
const TOGGLE_CONVERSION_ACTIVITY_PAST_DUE = 'TOGGLE_CONVERSION_ACTIVITY_PAST_DUE';
const TOGGLE_CONVERSION_ACTIVITY_TRIAL = 'TOGGLE_CONVERSION_ACTIVITY_TRIAL';

interface Conversion {
  id?: string;
  [key: string]: unknown;
}

interface PartnerOffering {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface ConversionOverviewResponse {
  conversions: Conversion[];
  partnerOfferings: PartnerOffering[];
}

interface State {
  conversionOverview: Conversion[];
  conversionPartnerOfferings: PartnerOffering[];
  filterTrial: boolean;
  filterFinal: boolean;
  filterPastDue: boolean;
  filterOneWeek: boolean;
  filterLater: boolean;
  filterComplete: boolean;
  modalConversion: Conversion;
}

const initialState: State = {
  conversionOverview: [],
  conversionPartnerOfferings: [],
  filterTrial: false,
  filterFinal: false,
  filterPastDue: false,
  filterOneWeek: false,
  filterLater: false,
  filterComplete: false,
  modalConversion: {},
};

interface Action {
  type: string;
  payload?: unknown;
  conversions?: ConversionOverviewResponse;
  status?: boolean;
}

export const reducer = (state = initialState, action: Action): State => {
  switch (action.type) {
    case SET_MODAL_CONVERSION:
      return {
        ...state,
        modalConversion: action.payload as Conversion,
      };
    case SET_CONVERSION_OVERVIEW:
      return {
        ...state,
        conversionOverview: action.conversions?.conversions || [],
        conversionPartnerOfferings: action.conversions?.partnerOfferings || [],
      };
    case TOGGLE_CONVERSION_ACTIVITY_CLEAR:
      return {
        ...state,
        filterTrial: false,
        filterFinal: false,
        filterPastDue: false,
        filterOneWeek: false,
        filterLater: false,
        filterComplete: false,
      };
    case TOGGLE_CONVERSION_ACTIVITY_TRIAL:
      return {
        ...state,
        filterTrial: action.status as boolean,
      };
    case TOGGLE_CONVERSION_ACTIVITY_FINAL:
      return {
        ...state,
        filterFinal: action.status as boolean,
      };
    case TOGGLE_CONVERSION_ACTIVITY_PAST_DUE:
      return {
        ...state,
        filterPastDue: action.status as boolean,
      };
    case TOGGLE_CONVERSION_ACTIVITY_ONE_WEEK:
      return {
        ...state,
        filterOneWeek: action.status as boolean,
      };
    case TOGGLE_CONVERSION_ACTIVITY_LATER:
      return {
        ...state,
        filterLater: action.status as boolean,
      };
    case TOGGLE_CONVERSION_ACTIVITY_COMPLETE:
      return {
        ...state,
        filterComplete: action.status as boolean,
      };
    default:
      return state;
  }
};

export const actions = (services: Services) => {
  const axiosService = services.axios.Axios();

  const setModalConversion = (conversion: Conversion) => {
    return {
      type: SET_MODAL_CONVERSION,
      payload: conversion,
    };
  };

  const updateMilestone = (milestoneId: string, type: string, fieldName: string, message = '') => {
    const typeLower = type.toLowerCase();
    return async (_dispatch: Dispatch) => {
      try {
        const payload = { fieldName: fieldName, message: message };
        await axiosService.patch(`conversions/${typeLower}/${milestoneId}/update`, payload);
      } catch (error) {
        throw error instanceof Error ? error.message : 'Unknown error';
      }
    };
  };

  const updateMilestoneTarget = (
    milestoneId: string,
    type: string,
    targetDate: string,
    message: string,
  ) => {
    const typeLower = type.toLowerCase();
    return async (_dispatch: Dispatch) => {
      try {
        const payload = {
          fieldName: type,
          targetDate: targetDate,
          message: message,
        };
        await axiosService.patch(`conversions/${typeLower}/${milestoneId}/target`, payload);
      } catch (error) {
        throw error instanceof Error ? error.message : 'Unknown error';
      }
    };
  };

  const setConversionsOverview = (conversions: ConversionOverviewResponse) => {
    return {
      type: SET_CONVERSION_OVERVIEW,
      conversions: conversions,
    };
  };

  const fetchConversionOverview = () => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosService.get<{ data: ConversionOverviewResponse }>(
          `conversions/overview`,
        );
        dispatch(setConversionsOverview(response.data.data));
        return response.data.data;
      } catch (error) {
        throw error instanceof Error ? error.message : 'Unknown error';
      }
    };
  };

  const clearConversionActivityFilters = () => {
    return {
      type: TOGGLE_CONVERSION_ACTIVITY_CLEAR,
    };
  };

  const setTrialFilter = (status = false) => {
    return {
      type: TOGGLE_CONVERSION_ACTIVITY_TRIAL,
      status: status,
    };
  };

  const setFinalFilter = (status = false) => {
    return {
      type: TOGGLE_CONVERSION_ACTIVITY_FINAL,
      status: status,
    };
  };

  const setPastDueFilter = (status = false) => {
    return {
      type: TOGGLE_CONVERSION_ACTIVITY_PAST_DUE,
      status: status,
    };
  };

  const setOneWeekFilter = (status = false) => {
    return {
      type: TOGGLE_CONVERSION_ACTIVITY_ONE_WEEK,
      status: status,
    };
  };

  const setLaterFilter = (status = false) => {
    return {
      type: TOGGLE_CONVERSION_ACTIVITY_LATER,
      status: status,
    };
  };

  const setCompleteFilter = (status = false) => {
    return {
      type: TOGGLE_CONVERSION_ACTIVITY_COMPLETE,
      status: status,
    };
  };

  const updateConvertAttachments = ({ id, flag }: { id: string; flag: boolean }) => {
    return async () => {
      try {
        await axiosService.patch(`conversions/${id}/convertattachments`, {
          convertAttachments: flag,
        });
      } catch (error) {
        throw error instanceof Error ? error.message : 'Unknown error';
      }
    };
  };

  const updateSourcePims = ({
    conversionId: id,
    sourcePims,
    sourcePimsName,
  }: {
    conversionId: string;
    sourcePims: string;
    sourcePimsName: string;
  }) => {
    return async () => {
      try {
        await axiosService.patch(`conversions/${id}/sourcepims`, {
          sourcePims: sourcePims,
          sourcePimsName: sourcePimsName,
        });
      } catch (error) {
        throw error instanceof Error ? error.message : 'Unknown error';
      }
    };
  };

  const resetConversion = (conversionId: string) => {
    return async () => {
      try {
        await axiosService.patch(`conversions/${conversionId}/reset`);
      } catch (error) {
        throw error instanceof Error ? error.message : 'Unknown error';
      }
    };
  };

  const setTrialExpedited = (trialId: string, isExpedited: boolean, message: string) => {
    return async () => {
      try {
        await axiosService.patch(`conversions/trial/${trialId}/expedited`, {
          isExpedited: isExpedited,
          message: message,
        });
      } catch (error) {
        throw error instanceof Error ? error.message : 'Unknown error';
      }
    };
  };

  const setFinalExpedited = (finalId: string, isExpedited: boolean, message: string) => {
    return async () => {
      try {
        await axiosService.patch(`conversions/final/${finalId}/expedited`, {
          isExpedited: isExpedited,
          message: message,
        });
      } catch (error) {
        throw error instanceof Error ? error.message : 'Unknown error';
      }
    };
  };

  return {
    setModalConversion,
    clearConversionActivityFilters,
    fetchConversionOverview,
    resetConversion,
    setCompleteFilter,
    setFinalFilter,
    setLaterFilter,
    setOneWeekFilter,
    setPastDueFilter,
    setTrialFilter,
    updateMilestone,
    updateMilestoneTarget,
    updateConvertAttachments,
    updateSourcePims,
    setTrialExpedited,
    setFinalExpedited,
  };
};
